import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import api, { withCompanyId } from "../API/Api";
import ToggleSlider from "../../Pages/ManageUser/ToggleSlider";

export default function InviteUserModal({ show, onHide }) {
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleStates, setRoleStates] = useState({});
  const [roleLoading, setRoleLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nameResults, setNameResults] = useState([]);
  const [nameLoading, setNameLoading] = useState(false);
  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  const nameFetchSeq = useRef(0);
  const nameDebounceRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_no: "",
    location_id: "",
  });

  const selectedRoles = useMemo(() => {
    return roles.filter((item) => roleStates[item.name]).map((item) => item.name);
  }, [roles, roleStates]);

  const canSubmit = useMemo(() => {
    return (
      selectedUser &&
      selectedRoles.length > 0
    );
  }, [selectedUser, selectedRoles]);

  useEffect(() => {
    if (!show) return;

    const fetchLocations = async () => {
      setLoadingLocations(true);
      try {
        const response = await api.get(`/v1/location`);
        const locationsData =
          response.data?.data?.data ||
          response.data?.data ||
          (Array.isArray(response.data) ? response.data : []);
        setLocations(Array.isArray(locationsData) ? locationsData : []);
      } catch (error) {
        setLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    const fetchRoles = async () => {
      setRoleLoading(true);
      try {
        const response = await api.get(`/v1/role/all_permissions`);
        const data = response.data?.data || [];
        setRoles(Array.isArray(data) ? data : []);
        const initialToggleStates = Object.fromEntries(
          (Array.isArray(data) ? data : []).map((role) => [role.name, false])
        );
        setRoleStates(initialToggleStates);
        setSelectAll(false);
      } catch (error) {
        setRoles([]);
        setRoleStates({});
        setSelectAll(false);
      } finally {
        setRoleLoading(false);
      }
    };

    fetchLocations();
    fetchRoles();
  }, [show]);

  // Name autocomplete using /v1/users/general_search?variable=...
  useEffect(() => {
    if (!show) return;
    const q = String(formData.name || "").trim();

    if (nameDebounceRef.current) {
      clearTimeout(nameDebounceRef.current);
      nameDebounceRef.current = null;
    }

    if (!q) {
      setNameResults([]);
      setNameLoading(false);
      return;
    }

    // small debounce to avoid spamming backend
    nameDebounceRef.current = setTimeout(async () => {
      const seq = ++nameFetchSeq.current;
      setNameLoading(true);
      try {
        const res = await api.get("/v1/users/general_search", {
          params: { variable: q },
        });
        // normalize results (API shape: { status, message, data: [{ id, name, ...}, ...] })
        const raw =
          (Array.isArray(res?.data?.data) ? res.data.data : null) ??
          (Array.isArray(res?.data?.data?.data) ? res.data.data.data : null) ??
          [];
        const names = raw
          .map((u) => ({
            id: u?.id ?? u?.user_id ?? u?.name,
            name: String(u?.name || "").trim(),
            email: String(u?.email || "").trim(),
            phone_no: String(u?.phone_no || u?.phone_number || "").trim(),
          }))
          .filter((x) => x.name);

        if (seq === nameFetchSeq.current) {
          setNameResults(names.slice(0, 10));
        }
      } catch (e) {
        if (seq === nameFetchSeq.current) setNameResults([]);
      } finally {
        if (seq === nameFetchSeq.current) setNameLoading(false);
      }
    }, 250);

    return () => {
      if (nameDebounceRef.current) {
        clearTimeout(nameDebounceRef.current);
        nameDebounceRef.current = null;
      }
    };
  }, [formData.name, show]);

  const handleSelectAllChange = () => {
    const next = !selectAll;
    const updated = {};
    roles.forEach((r) => {
      updated[r.name] = next;
    });
    setRoleStates(updated);
    setSelectAll(next);
  };

  const handleRoleToggleChange = (permissionName) => {
    setRoleStates((prev) => ({ ...prev, [permissionName]: !prev[permissionName] }));
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone_no: "", location_id: "" });
    setNameResults([]);
    setNameLoading(false);
    setNameDropdownOpen(false);
    setSelectedUser(null);
    const resetRoles = {};
    roles.forEach((r) => {
      resetRoles[r.name] = false;
    });
    setRoleStates(resetRoles);
    setSelectAll(false);
  };

  const handleClose = () => {
    if (typeof onHide === "function") onHide();
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);

    try {
      // Send invitation
      const invitePayload = { user_id: selectedUser.id };
      const inviteBody = withCompanyId(invitePayload);
      const response = await api.post('/v1/invitation/by_company', inviteBody);

      await Swal.fire({
        icon: "success",
        title: "User Invited",
        text: response?.data?.message || "User invited successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      handleClose();
    } catch (error) {
      const errorMessage =
        typeof error.response?.data?.message === "object"
          ? Object.values(error.response.data.message).flat().join("\n")
          : error.response?.data?.message || error.message || "Something went wrong.";

      await Swal.fire({
        icon: "error",
        title: "Failed to Invite User",
        text: errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={!!show} onHide={handleClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Invite User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Name</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  name="name"
                  placeholder="Search name..."
                  value={formData.name}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, name: e.target.value }));
                    setNameDropdownOpen(true);
                  }}
                  onFocus={() => setNameDropdownOpen(true)}
                  onBlur={() => {
                    // allow click selection before closing
                    setTimeout(() => setNameDropdownOpen(false), 140);
                  }}
                  required
                  style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
                />
                {nameDropdownOpen && (nameLoading || nameResults.length > 0) && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                      zIndex: 20,
                      maxHeight: 260,
                      overflowY: "auto",
                    }}
                  >
                    {nameLoading ? (
                      <div style={{ padding: 10, color: "#6b7280", fontSize: 13 }}>
                        Searching...
                      </div>
                    ) : (
                      nameResults.map((r) => (
                        <button
                          key={String(r.id)}
                          type="button"
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => {
                            setFormData((p) => ({
                              ...p,
                              name: r.name,
                              // optionally prefill if backend returns these
                              email: r.email || p.email,
                              phone_no: r.phone_no || p.phone_no,
                            }));
                            setSelectedUser(r);
                            setNameDropdownOpen(false);
                          }}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "10px 12px",
                            border: 0,
                            background: "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                            {r.name}
                          </div>
                          {(r.email || r.phone_no) && (
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                              {[r.email, r.phone_no].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Phone Number</label>
              <input
                type="tel"
                name="phone_no"
                placeholder="Enter Phone Number"
                value={formData.phone_no}
                onChange={(e) => setFormData((p) => ({ ...p, phone_no: e.target.value }))}
                required
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Email</label>
              <input
                type="email"
                name="email"
                placeholder="Enter Email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                required
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Location (Optional)</label>
              <select
                name="location_id"
                value={formData.location_id}
                onChange={(e) => setFormData((p) => ({ ...p, location_id: e.target.value }))}
                disabled={loadingLocations}
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
              >
                <option value="">
                  {loadingLocations ? "Loading locations..." : "Select Location"}
                </option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.location_address || loc.name || `Location ${loc.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>Roles & Permissions</span>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Select All</span>
                <ToggleSlider checked={selectAll} onChange={handleSelectAllChange} />
              </div>
            </div>

            {roleLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
                <Spinner size="sm" animation="border" role="status" />
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {roles.map((item) => (
                  <div
                    key={item.id || item.name}
                    style={{
                      width: 90,
                      minHeight: 62,
                      padding: 5,
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 10,
                      background: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                    }}
                  >
                    <div style={{ fontSize: 10, textAlign: "center", lineHeight: 1.1 }}>
                      {item.name}
                    </div>
                    <ToggleSlider
                      checked={!!roleStates[item.name]}
                      onChange={() => handleRoleToggleChange(item.name)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "transparent",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !canSubmit}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: 0,
                background: "#16A34A",
                color: "#fff",
                fontWeight: 700,
                opacity: submitting || !canSubmit ? 0.7 : 1,
                cursor: submitting || !canSubmit ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Spinner size="sm" animation="border" /> Inviting...
                </span>
              ) : (
                "Invite User"
              )}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

